const clamp = (value, min = 0, max = 100) => Math.max(min, Math.min(max, Math.round(value)));
const val = (array, index, fallback = 0) => Number(array?.[index] ?? fallback) || 0;
const fmtTime = (time) => time ? new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

function hourScore({ wind, gust, wave, rain, visibility }) {
  const gustSpread = Math.max(0, gust - wind);
  const visibilityPenalty = visibility && visibility < 5000 ? 12 : 0;
  return clamp(100 - wind * 1.35 - wave * 16 - gustSpread * 1.7 - rain * 0.18 - visibilityPenalty);
}

function safetyTone(score) {
  if (score >= 78) return { label: "Green", tone: "green" };
  if (score >= 58) return { label: "Yellow", tone: "yellow" };
  if (score >= 38) return { label: "Orange", tone: "orange" };
  return { label: "Red", tone: "red" };
}

export function buildSailingIntelligence({ forecast, marine, windKnots, gustKnots, waveM }) {
  const cur = forecast?.current ?? {};
  const hourly = forecast?.hourly ?? {};
  const marineHourly = marine?.hourly ?? {};
  const times = hourly.time || [];
  const now = Date.now();
  const start = Math.max(0, times.findIndex((time) => new Date(time).getTime() >= now - 30 * 60 * 1000));
  const currentScore = hourScore({ wind: windKnots, gust: gustKnots, wave: waveM, rain: val(hourly.precipitation_probability, start, 0), visibility: cur.visibility });
  const hourlyScores = times.slice(start, start + 12).map((time, offset) => {
    const i = start + offset;
    return {
      time,
      score: hourScore({ wind: val(hourly.wind_speed_10m, i, windKnots), gust: val(hourly.wind_gusts_10m, i, gustKnots), wave: val(marineHourly.wave_height, i, waveM), rain: val(hourly.precipitation_probability, i, 0), visibility: val(hourly.visibility, i, cur.visibility) }),
    };
  });
  const best = hourlyScores.reduce((top, item) => item.score > top.score ? item : top, hourlyScores[0] || { time: null, score: currentScore });
  const deterioration = hourlyScores.find((item, index) => index > 0 && currentScore - item.score >= 15);
  const pressureNow = val(hourly.pressure_msl, start, cur.pressure_msl);
  const pressureSoon = val(hourly.pressure_msl, start + 2, pressureNow);
  const comfort = clamp(100 - Math.abs((cur.temperature_2m ?? 22) - 22) * 2 - windKnots * 0.9 - waveM * 20 - Math.max(0, (cur.relative_humidity_2m ?? 65) - 75) * 0.35);
  const anchoring = clamp(100 - windKnots * 1.8 - Math.max(0, gustKnots - windKnots) * 2.2 - waveM * 24);
  const sickness = clamp(waveM * 26 + val(marine?.current?.swell_wave_height ? [marine.current.swell_wave_height] : [], 0, 0) * 18 + Math.max(0, windKnots - 12) * 1.6);
  const risk = clamp(100 - currentScore + (cur.precipitation > 0 ? 8 : 0) + (pressureSoon - pressureNow <= -2 ? 10 : 0));
  const confidence = clamp(94 - Math.max(0, gustKnots - windKnots) * 0.9 - waveM * 4 - (times.length < 12 ? 12 : 0), 55, 98);
  const weatherChangeMinutes = deterioration ? Math.max(90, Math.round((new Date(deterioration.time).getTime() - now) / 60000)) : null;
  return {
    sailingScore: currentScore,
    safety: safetyTone(currentScore),
    comfort,
    recommendedDeparture: fmtTime(best.time),
    anchoring,
    dockingDifficulty: gustKnots >= 28 || windKnots >= 22 ? "High" : windKnots >= 15 || waveM >= 1.2 ? "Moderate" : "Low",
    seaSickness: sickness,
    forecastConfidence: confidence,
    routeRisk: risk,
    weatherChange: { deteriorating: Boolean(weatherChangeMinutes), minutes: weatherChangeMinutes },
    weatherChangeAlert: weatherChangeMinutes ? `Conditions expected to deteriorate in ${weatherChangeMinutes} minutes` : "No major deterioration expected in the next 3 hours",
  };
}